import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/**
 * A two-part control for tap-to-advance conductor mode.
 * Left side: toggle button to enable/disable tap-advance mode.
 * Right side: large tappable area that fires on each tap.
 *
 * @fires tap-advance-toggle - Fired when tap-advance mode is toggled (detail: { enabled: boolean })
 * @fires tap-advance - Fired on each tap of the tap area
 * @fires tap-advance-brake - Fired when the brake button is pressed
 */
@customElement('tap-advance-control')
export class TapAdvanceControl extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
            }

            .btn-group {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            .toggle-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                background: #333;
                border: none;
                border-radius: 1rem;
                padding: 0.25rem 0.5rem;
                color: #999;
                font-size: 0.85rem;
                cursor: pointer;
                transition: all 0.2s;
            }

            .toggle-btn:hover:not([disabled]) {
                background: #444;
            }

            .toggle-btn[disabled] {
                opacity: 0.5;
                cursor: not-allowed;
            }

            :host([enabled]) .toggle-btn {
                background: var(--primary-color, #2563eb);
                color: white;
            }

            :host([enabled]) .toggle-btn:hover:not([disabled]) {
                background: var(--primary-dark, #1e40af);
            }

            .brake-btn {
                display: none;
                align-items: center;
                justify-content: center;
                min-width: 48px;
                min-height: 48px;
                background: #553333;
                border: none;
                border-radius: 0.75rem;
                padding: 0.25rem 0.5rem;
                color: #ccc;
                font-size: 0.75rem;
                cursor: pointer;
                transition: all 0.15s;
                touch-action: manipulation;
                -webkit-tap-highlight-color: transparent;
            }

            :host([enabled]) .brake-btn {
                display: flex;
            }

            .brake-btn:active {
                background: #cc3333;
                color: white;
            }

            .tap-area {
                display: none;
                align-items: center;
                justify-content: center;
                min-width: 120px;
                min-height: 120px;
                padding: 0.5rem 2rem;
                background: #444;
                border: 2px solid #666;
                border-radius: 0.75rem;
                color: #ccc;
                font-size: 1.1rem;
                font-weight: 700;
                letter-spacing: 0.1em;
                cursor: pointer;
                user-select: none;
                touch-action: manipulation;
                -webkit-tap-highlight-color: transparent;
                transition: transform 0.08s, background 0.08s;
            }

            :host([enabled]) .tap-area {
                display: flex;
            }

            .tap-area:active {
                transform: scale(0.93);
                background: var(--primary-color, #2563eb);
                border-color: var(--primary-color, #2563eb);
                color: white;
            }

            .tap-area[disabled] {
                opacity: 0.5;
                cursor: not-allowed;
            }
        `,
    ];

    @property({ type: Boolean, reflect: true })
    enabled = false;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    private _handleToggle(): void {
        if (this.disabled) return;
        this.enabled = !this.enabled;
        this.dispatchEvent(
            new CustomEvent('tap-advance-toggle', {
                bubbles: true,
                composed: true,
                detail: { enabled: this.enabled },
            })
        );
    }

    private _handleBrake(): void {
        if (this.disabled || !this.enabled) return;
        this.dispatchEvent(
            new CustomEvent('tap-advance-brake', {
                bubbles: true,
                composed: true,
            })
        );
    }

    private _handleTap(e: PointerEvent): void {
        if (this.disabled || !this.enabled) return;
        e.preventDefault();
        this.dispatchEvent(
            new CustomEvent('tap-advance', {
                bubbles: true,
                composed: true,
            })
        );
    }

    render() {
        return html`
            <div class="btn-group">
                <button
                    class="toggle-btn"
                    ?disabled=${this.disabled}
                    @click=${this._handleToggle}
                    aria-pressed=${this.enabled}
                >
                    <span>Tap</span>
                </button>
                <button
                    class="brake-btn"
                    ?disabled=${this.disabled}
                    @click=${this._handleBrake}
                >
                    Brake
                </button>
            </div>
            <div
                class="tap-area"
                ?disabled=${this.disabled}
                @pointerdown=${this._handleTap}
            >
                TAP
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'tap-advance-control': TapAdvanceControl;
    }
}
